/**
 * The starts going on in this tab, by key (`project:<id>`, `service:<id>:<service>`, `bulk`), outside any component:
 * a start goes on, and its button shows it, when the button is mounted again (the Home's tools move between the strip
 * and the tab row, a list row scrolls away and back, the person leaves the page and comes back). The project Home and
 * the Projects list's row of the same project show the same start.
 */
import { useSyncExternalStore } from 'react';

const runs = new Map(); // key -> { progress, running }
const stopRequests = new Set(); // keys whose start the person stopped meanwhile
const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const projectStartKey = (projectId) => `project:${projectId}`;
export const serviceStartKey = (projectId, serviceName) => `service:${projectId}:${serviceName}`;

/** The progress of the start under `key` (the last one, until cleared), or null. */
export function startProgressOf(key) {
  return runs.get(key)?.progress || null;
}

/** True while the start under `key` goes on. */
export function isStarting(key) {
  return Boolean(runs.get(key)?.running);
}

/** The person pressed Stop while the start under `key` goes on: it starts no more services. */
export function stopStart(key) {
  if (isStarting(key)) { stopRequests.add(key); }
}

/**
 * Runs `start(onProgress, shouldStop)` (a start that reports its progress and resolves to the last one) under `key`,
 * unless one is already going on there (then resolves to null). `shouldStop()` turns true once `stopStart(key)` was
 * called. Resolves to what `start` resolved to.
 */
export async function runStart(key, start) {
  if (isStarting(key)) { return null; }
  stopRequests.delete(key);
  runs.set(key, { progress: runs.get(key)?.progress || null, running: true });
  notify();
  const onProgress = (progress) => {
    runs.set(key, { progress, running: true });
    notify();
  };
  try {
    return await start(onProgress, () => stopRequests.has(key));
  } finally {
    stopRequests.delete(key);
    runs.set(key, { progress: runs.get(key)?.progress || null, running: false });
    notify();
  }
}

/** Forgets the last start under `key` (its button is back to its label), unless it still goes on. */
export function clearStart(key) {
  if (isStarting(key) || !runs.has(key)) { return; }
  runs.delete(key);
  notify();
}

/** `{ progress, starting }` of the start under `key`, kept in step. */
export function useStart(key) {
  const progress = useSyncExternalStore(subscribe, () => startProgressOf(key), () => null);
  const starting = useSyncExternalStore(subscribe, () => isStarting(key), () => false);
  return { progress, starting };
}

/** Tests: forget every start. */
export function resetStarts() {
  runs.clear();
  stopRequests.clear();
  notify();
}
